import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { getCategoryName } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 pb-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.slug, article.categorySlug, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <article>
          <div className="bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: getCategoryName(article.categorySlug), href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-xs px-3 py-1 rounded-full bg-primary-100/70 text-primary-700 font-medium hover:bg-primary-200 transition-colors"
                >
                  {getCategoryName(article.categorySlug)}
                </Link>
                {article.isNew && (
                  <span className="text-xs px-3 py-1 rounded-full bg-accent-100/70 text-accent-700 font-medium">
                    新品
                  </span>
                )}
                {article.isHot && (
                  <span className="text-xs px-3 py-1 rounded-full bg-secondary-100/70 text-secondary-700 font-medium">
                    热门
                  </span>
                )}
                {article.tags.map((tag, i) => (
                  <span key={i} className="text-xs text-foreground-500">
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200/60">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary-100/70 flex items-center justify-center">
                    <span className="text-xs font-semibold text-primary-600">
                      {article.author.charAt(0)}
                    </span>
                  </div>
                  <span>{article.author}</span>
                </div>
                <span>·</span>
                <span>{article.publishDate}</span>
                <span>·</span>
                <span>{article.readTime}</span>
                {article.views && (
                  <>
                    <span>·</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.views > 1000
                        ? `${(article.views / 1000).toFixed(1)}k 阅读`
                        : `${article.views} 阅读`}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="mb-10 rounded-xl overflow-hidden aspect-[16/9]">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 第一次人妻完全指南`}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/60">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 border border-background-200/60"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} {...a} variant="default" views={undefined} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.publishDate,
            image: article.imageUrl,
            publisher: {
              '@type': 'Organization',
              name: '第一次人妻完全指南',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://firstwife.guide/' },
                { '@type': 'ListItem', position: 2, name: getCategoryName(article.categorySlug), item: `https://firstwife.guide/category/${article.categorySlug}` },
                { '@type': 'ListItem', position: 3, name: article.title },
              ],
            },
          }),
        }}
      />
    </>
  );
}